import { ReactNode } from 'react';
import { DialogProps, Heading } from 'react-aria-components';
import { twMerge } from 'tailwind-merge';
import Tag from 'src/components/ui/Tag';
import Dialog from 'src/components/ui/Dialog';
import Button from 'src/components/ui/Button';
import TextLabel from 'src/components/ui/TextLabel';
import IconButton from 'src/components/ui/IconButton';
import LinkButton from 'src/components/ui/LinkButton';
import ProgressBar from 'src/components/ui/ProgressBar';
import CloseIcon from 'src/assets/icons/close.svg';
import CheckIcon from 'src/assets/icons/check.svg';
import TestResultsIcon from 'src/assets/icons/school-test-results.svg';
import HatIcon from 'src/assets/icons/graduation-hat.svg';
import FileIcon from 'src/assets/icons/file-paper-document.svg';
import BookIcon from 'src/assets/icons/book-open-2.svg';
import CourseThumbnail from 'src/assets/illustrations/no-thumbnail.svg';

import type { Size } from 'src/types/common';
import type { CourseShort } from 'src/types/course';

interface CourseDialogProps extends Omit<DialogProps, 'children'> {
  course: CourseShort;
  size?: Size;
  onView: () => void;
  onRead: () => void;
}

interface CourseBaseProps {
  children?: ReactNode;
  className?: string;
}

interface CourseHeaderProps extends CourseBaseProps {
  course: CourseShort;
}

interface CourseFooterProps extends CourseBaseProps {
  isSmall?: boolean;
}

export const CourseDialogHeader = ({
  course,
  className,
  children,
}: CourseHeaderProps) => (
  <header
    className={twMerge(
      'relative bg-surface text-surface-90 border-b border-surface-10 p-6 pb-2',
      className
    )}
  >
    <Heading slot="title" level={4} className="text-sm font-medium uppercase">
      {course?.term?.name ?? ''}
    </Heading>
    <strong className="block text-xl font-bold mt-4">{course.name}</strong>
    <span className="block -mt-0.5 text-sm font-medium">
      {(course?.professors ?? [])
        .map((p) => p.name)
        .filter(Boolean)
        .join(', ') || '-'}
    </span>
    <div className="flex gap-4 py-2.5 pr-12">
      {course.subject?.name && <Tag title={course.subject.name} />}
      <TextLabel
        prefix={`${course.courseMaterials.length}`}
        label={course.courseMaterials.length > 1 ? 'Files' : 'File'}
        Icon={FileIcon}
      />
    </div>
    {children}
  </header>
);

export const CourseDialogBody = ({ className, children }: CourseBaseProps) => (
  <section className={twMerge('px-6 pt-4 pb-3', className)}>{children}</section>
);

export const CourseDialogFooter = ({
  isSmall = false,
  className,
  children,
}: CourseFooterProps) => (
  <footer
    className={twMerge(
      `flex justify-end items-center gap-4 p-6 pt-0 ${isSmall ? 'flex-col items-stretch' : 'flex-row'}`,
      className
    )}
  >
    {children}
  </footer>
);

const CourseInfoDialog = ({
  size = 'medium',
  course,
  onView,
  onRead,
  className,
  ...props
}: CourseDialogProps) => {
  const isSmall = size === 'small';

  return (
    <Dialog className={twMerge('p-0 overflow-hidden', className)} {...props}>
      {({ close }) => (
        <>
          <div
            className={`flex items-stretch max-h-[inherit] ${isSmall ? 'flex-col' : ''}`}
          >
            <div
              className={`relative flex items-center overflow-hidden bg-secondary-ghost-alt flex-shrink-0 ${isSmall ? 'h-80' : 'w-71'}`}
            >
              <img
                className="w-full h-auto"
                src={course.image || CourseThumbnail}
                alt={`${course.name} Cover`}
              />
            </div>
            <div className="flex-1 min-h-0 overflow-auto flex flex-col">
              <CourseDialogHeader course={course} />
              <CourseDialogBody className="flex-1">
                <div
                  className={`grid ${isSmall ? 'grid-cols-1' : 'grid-cols-2'} gap-2.5`}
                >
                  <div>
                    <TextLabel label="Class" Icon={TestResultsIcon} />
                    <p className="text-surface-alt-90 text-md font-semibold">
                      {course.identifier || '-'}
                    </p>
                  </div>
                  <div>
                    <TextLabel label="Subject" Icon={HatIcon} />
                    <p className="text-surface-alt-90 text-md font-semibold">
                      {course.subject?.name ?? '-'}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-2 mt-4">
                  <TextLabel label="Progress" Icon={CheckIcon} />
                  <ProgressBar
                    aria-label="Course Progress"
                    value={Number(course.progress)}
                  />
                </div>
              </CourseDialogBody>
              <CourseDialogFooter isSmall={isSmall}>
                <LinkButton className="justify-center" onPress={onView}>
                  View Details
                </LinkButton>
                <Button
                  className="justify-center"
                  onPress={onRead}
                  IconLeft={BookIcon}
                >
                  Open the Course Pack
                </Button>
              </CourseDialogFooter>
            </div>
          </div>
          <IconButton
            aria-label="Close"
            isFlat={isSmall}
            className={`absolute ${isSmall ? 'bg-surface-alt right-2 top-2.5' : 'right-3 top-3'}`}
            Icon={CloseIcon}
            onPress={close}
          />
        </>
      )}
    </Dialog>
  );
};

export default CourseInfoDialog;
